import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  IconButton,
  Alert,
  CircularProgress,
  Divider,
  Snackbar,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  CompanyEmployee,
  CompanyEmployeeRole,
  EmployeeStatus,
  UpdateEmployeeFormData,
} from '../../types/employee';
import employeeService from '../../services/employeeService';
import { RoleChangeConfirmDialog } from './RoleChangeConfirmDialog';
import { StatusChangeConfirmDialog } from './StatusChangeConfirmDialog';
import { useAuth } from '../../hooks/useAuth';
import ChangeEmailModal from '../User/ChangeEmailModal';

// バリデーションスキーマ
const updateEmployeeSchema = yup.object({
  fullName: yup
    .string()
    .required('氏名を入力してください')
    .max(50, '氏名は50文字以内で入力してください')
    .trim(),
  department: yup
    .string()
    .max(50, '部署名は50文字以内で入力してください')
    .trim(),
  jobTitle: yup.string().max(50, '役職名は50文字以内で入力してください').trim(),
  role: yup
    .string()
    .required('権限を選択してください')
    .oneOf(Object.values(CompanyEmployeeRole), '正しい権限を選択してください'),
});

type UpdateEmployeeFormValues = yup.InferType<typeof updateEmployeeSchema>;

interface EmployeeEditModalProps {
  open: boolean;
  employee: CompanyEmployee | null;
  companyId: number;
  onClose: () => void;
  onSuccess: (updatedEmployee: CompanyEmployee) => void;
  onEmailChanged?: () => void;
}

export const EmployeeEditModal: React.FC<EmployeeEditModalProps> = ({
  open,
  employee,
  companyId,
  onClose,
  onSuccess,
  onEmailChanged,
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isActive, setIsActive] = useState(true);
  const [initialIsActive, setInitialIsActive] = useState(true);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  // Phase 2 Week 2: ロール変更確認ダイアログ
  const [roleChangeConfirmOpen, setRoleChangeConfirmOpen] = useState(false);
  const [pendingRoleChange, setPendingRoleChange] = useState<{
    data: UpdateEmployeeFormValues;
    originalRole: CompanyEmployeeRole;
  } | null>(null);
  // ステータス変更確認ダイアログ
  const [statusChangeConfirmOpen, setStatusChangeConfirmOpen] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState<{
    data: UpdateEmployeeFormValues;
    originalStatus: boolean;
  } | null>(null);
  const [isLastAdmin, setIsLastAdmin] = useState(false);
  // Phase 2 Week 2: トースト通知
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<
    'success' | 'error' | 'info' | 'warning'
  >('success');

  // 自分のプロフィールかどうかを判定
  const isOwnProfile = user?.id === employee?.user?.id;

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isDirty },
  } = useForm<UpdateEmployeeFormValues>({
    resolver: yupResolver(updateEmployeeSchema) as any,
    defaultValues: {
      fullName: '',
      department: '',
      jobTitle: '',
      role: CompanyEmployeeRole.EMPLOYEE,
    },
  });

  // Phase 2 Week 2: ロール変更を監視
  const watchedRole = watch('role');

  // 従業員データが変更されたときにフォームをリセット
  useEffect(() => {
    if (employee) {
      const activeStatus = employee.status === EmployeeStatus.ACTIVE;
      reset({
        fullName: employee.user.fullName,
        department: employee.department || '',
        jobTitle: employee.jobTitle || '',
        role: employee.role,
      });
      setIsActive(activeStatus);
      setInitialIsActive(activeStatus);
    }
  }, [employee, reset]);

  const handleClose = () => {
    setError(null);
    setRoleChangeConfirmOpen(false);
    setPendingRoleChange(null);
    setStatusChangeConfirmOpen(false);
    setPendingStatusChange(null);
    setIsLastAdmin(false);
    onClose();
  };

  // Phase 2 Week 2: トースト通知を表示
  const showSnackbar = (
    message: string,
    severity: 'success' | 'error' | 'info' | 'warning'
  ) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // Phase 2 Week 2: 実際の更新処理
  const executeUpdate = async (data: UpdateEmployeeFormValues) => {
    if (!employee) return;

    setLoading(true);
    setError(null);

    try {
      const updateData: UpdateEmployeeFormData = {
        fullName: data.fullName,
        department: data.department || undefined,
        jobTitle: data.jobTitle || undefined,
        role: data.role as CompanyEmployeeRole,
        status: isActive ? EmployeeStatus.ACTIVE : EmployeeStatus.INACTIVE,
      };

      const response = await employeeService.updateEmployee(
        companyId,
        employee.id,
        updateData
      );

      if (response.success) {
        showSnackbar('従業員情報を更新しました', 'success');
        onSuccess(response.data.employee);
        handleClose();
      }
    } catch (err: any) {
      console.error('従業員更新エラー:', err);
      const errorMessage = err.message || '従業員情報の更新に失敗しました';
      setError(errorMessage);
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Phase 2 Week 2: ロール変更確認ダイアログの処理
  const handleRoleChangeConfirm = async () => {
    if (pendingRoleChange) {
      setRoleChangeConfirmOpen(false);
      await executeUpdate(pendingRoleChange.data);
      setPendingRoleChange(null);
    }
  };

  const handleRoleChangeCancel = () => {
    setRoleChangeConfirmOpen(false);
    setPendingRoleChange(null);
  };

  // ステータス変更確認ダイアログの処理
  const handleStatusChangeConfirm = async () => {
    if (pendingStatusChange) {
      setStatusChangeConfirmOpen(false);
      await executeUpdate(pendingStatusChange.data);
      setPendingStatusChange(null);
      setIsLastAdmin(false);
    }
  };

  const handleStatusChangeCancel = () => {
    setStatusChangeConfirmOpen(false);
    setPendingStatusChange(null);
    setIsLastAdmin(false);
    // 元の状態に戻す
    if (pendingStatusChange) {
      setIsActive(pendingStatusChange.originalStatus);
    }
  };

  // Phase 2 Week 2: 送信時にロール変更を確認
  const onSubmit = async (data: UpdateEmployeeFormValues) => {
    if (!employee) return;

    const statusChanged = isActive !== initialIsActive;
    const roleChanged = data.role !== employee.role;

    // ステータス変更がある場合のチェック
    if (
      statusChanged &&
      !isActive && // 無効化する場合
      employee.role === CompanyEmployeeRole.COMPANY_ADMIN &&
      employee.status === EmployeeStatus.ACTIVE
    ) {
      // 最後の企業管理者かどうかをチェック（専用エンドポイント使用）
      try {
        const { isLastAdmin: isLastAdminUser } = await employeeService.checkIsLastAdmin(
          companyId,
          employee.id
        );
        setIsLastAdmin(isLastAdminUser);

        // 最後の企業管理者の場合は警告ダイアログを表示
        if (isLastAdminUser) {
          setPendingStatusChange({
            data,
            originalStatus: initialIsActive,
          });
          setStatusChangeConfirmOpen(true);
          return;
        }
      } catch (error) {
        console.error('企業管理者数の確認に失敗しました:', error);
        showSnackbar(
          '管理者数の確認に失敗しました。ネットワークを確認の上、再度お試しください。',
          'error'
        );
        return; // エラー時は処理を中断
      }
    }

    // ロールが変更されている場合は確認ダイアログを表示
    if (roleChanged) {
      setPendingRoleChange({
        data,
        originalRole: employee.role,
      });
      setRoleChangeConfirmOpen(true);
    } else {
      // ロール変更がない場合は直接更新
      await executeUpdate(data);
    }
  };

  if (!employee) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle sx={{ pb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <EditIcon sx={{ fontSize: 28, color: 'primary.main', mr: 1 }} />
            <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
              従業員情報編集
            </Typography>
          </Box>
          <IconButton onClick={handleClose} sx={{ color: 'text.secondary' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider />

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent sx={{ pt: 3 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* メールアドレス情報 */}
          {isOwnProfile && (
            <Box
              sx={{
                mb: 3,
                p: 2,
                bgcolor: 'grey.50',
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                <EmailIcon sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }} />
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ 
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {employee.user?.email}
                </Typography>
              </Box>
              <Button
                onClick={() => setEmailModalOpen(true)}
                size="small"
                variant="text"
                sx={{
                  fontSize: '0.75rem',
                  minWidth: 'fit-content',
                  px: 1.5,
                  py: 0.5,
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}
              >
                メール変更
              </Button>
            </Box>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* 氏名 */}
            <Controller
              name="fullName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="氏名"
                  required
                  fullWidth
                  error={!!errors.fullName}
                  helperText={errors.fullName?.message}
                  disabled={loading}
                />
              )}
            />

            {/* 部署 */}
            <Controller
              name="department"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="部署"
                  fullWidth
                  error={!!errors.department}
                  helperText={errors.department?.message}
                  disabled={loading}
                  placeholder="例: 営業部"
                />
              )}
            />

            {/* 役職 */}
            <Controller
              name="jobTitle"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="役職"
                  fullWidth
                  error={!!errors.jobTitle}
                  helperText={errors.jobTitle?.message}
                  disabled={loading}
                  placeholder="例: 主任"
                />
              )}
            />

            {/* 権限 - Phase 2 Week 2: HRロール追加 */}
            <Controller
              name="role"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth error={!!errors.role} disabled={loading}>
                  <InputLabel>権限</InputLabel>
                  <Select {...field} label="権限">
                    <MenuItem value={CompanyEmployeeRole.COMPANY_ADMIN}>
                      企業管理者
                    </MenuItem>
                    <MenuItem value={CompanyEmployeeRole.HR}>
                      人事担当者
                    </MenuItem>
                    <MenuItem value={CompanyEmployeeRole.EMPLOYEE}>
                      一般従業員
                    </MenuItem>
                  </Select>
                  {errors.role && (
                    <FormHelperText>{errors.role.message}</FormHelperText>
                  )}
                  {/* Phase 2 Week 2: ロール説明 */}
                  {watchedRole === CompanyEmployeeRole.COMPANY_ADMIN && (
                    <FormHelperText>
                      全機能へのアクセス、従業員管理、削除時は全従業員が連動削除
                    </FormHelperText>
                  )}
                  {watchedRole === CompanyEmployeeRole.HR && (
                    <FormHelperText>
                      企業管理者と同等の権限、削除時は本人のみ削除（連動なし）
                    </FormHelperText>
                  )}
                  {watchedRole === CompanyEmployeeRole.EMPLOYEE && (
                    <FormHelperText>
                      自分の情報の閲覧・編集のみ
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            />

            <Divider sx={{ my: 2 }} />

            {/* アカウントステータス */}
            <FormControlLabel
              control={
                <Switch
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  disabled={loading}
                />
              }
              label="アカウントを有効にする"
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            color="inherit"
            disabled={loading}
            sx={{ minWidth: 100 }}
          >
            キャンセル
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading || (!isDirty && isActive === initialIsActive)}
            sx={{ minWidth: 100 }}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : '更新'}
          </Button>
        </DialogActions>
      </form>

      {/* Phase 2 Week 2: ロール変更確認ダイアログ */}
      <RoleChangeConfirmDialog
        open={roleChangeConfirmOpen}
        employeeName={employee?.user?.fullName || ''}
        currentRole={pendingRoleChange?.originalRole || CompanyEmployeeRole.EMPLOYEE}
        newRole={(pendingRoleChange?.data.role as CompanyEmployeeRole) || CompanyEmployeeRole.EMPLOYEE}
        isLoading={loading}
        onClose={handleRoleChangeCancel}
        onConfirm={handleRoleChangeConfirm}
      />

      {/* ステータス変更確認ダイアログ */}
      <StatusChangeConfirmDialog
        open={statusChangeConfirmOpen}
        employeeName={employee?.user?.fullName || ''}
        isLastAdmin={isLastAdmin}
        isDeactivating={!isActive}
        isLoading={loading}
        onClose={handleStatusChangeCancel}
        onConfirm={handleStatusChangeConfirm}
      />

      {/* Phase 2 Week 2: トースト通知 */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* メールアドレス変更モーダル */}
      {isOwnProfile && employee && (
        <ChangeEmailModal
          open={emailModalOpen}
          onClose={() => setEmailModalOpen(false)}
          currentEmail={employee.user?.email || ''}
          onSuccess={(newEmail) => {
            setEmailModalOpen(false);
            if (onEmailChanged) {
              onEmailChanged();
            }
          }}
        />
      )}
    </Dialog>
  );
};
